import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { CloudDownload as DownloadIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { TableVirtuoso } from 'react-virtuoso';
import { authGet, authPost } from '../../../services/apiService';
import { isAdmin } from '../../../services/authService';
import { MUTED, INK, NEUTRAL_SURFACE } from '../colorTokens';
import { DONUT_TABS, REPORT_TAB_INDEX } from './DonutCards';
import { IconActionButton } from './IconActionButton';
import { PopupDialog } from './MonitorPopups';

const API = '/api/datadog-monitors/report-batches';
const MONTHLY_REPORT_API = '/api/datadog-monitors/monthly-report';
const EPISODES_API = '/api/datadog-monitors/episodes';
const ACCENT = DONUT_TABS[REPORT_TAB_INDEX].color;

// Days are calendar days at a fixed UTC-5 offset (the server applies it; see REPORT_UTC_OFFSET_HOURS in
// datadogEpisodeBatchService.js). A fetch covers one whole month, the first being July 2026.
const DATE_FORMAT = 'YYYY-MM-DD';
const MONTH_KEY_FORMAT = 'YYYY-MM';
const FIRST_MONTH = dayjs('2026-07-01');
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const POLL_MS = 3000;
const MAX_POLL_FAILURES = 5;

/** The latest month that can be fetched: the one before the current month (UTC-5), as a dayjs on its first day. */
const lastFetchableMonth = () => dayjs(`${new Date(Date.now() - 5 * 3600000).toISOString().slice(0, 7)}-01`).subtract(1, 'month');

// What Fetch monitor events checks before it runs; shown as its tooltip. Keep in step with resolveMonth / createBatch
// in datadogEpisodeBatchService.js.
const FETCH_CHECKS = [
  'A month and a year are picked: July 2026 up to the month before the current one (UTC-5).',
  'The whole month is fetched, never part of one.',
  'A month that was fetched already cannot be picked again, so events are never fetched twice.',
  'A month that has only just ended has to wait about an hour, so events that arrive late are included.',
  'Only one fetch can run at a time.'
];

// Same approach as IconActionButton's tooltip: CSS-positioned, because MUI's Popper is thrown off by the
// app's `body { zoom: 0.8 }`. The wrapper span keeps hover and focus working while the button is disabled.
const FETCH_TIP_WRAPPER_SX = {
  position: 'relative',
  display: 'inline-flex',
  '& .fetch-tip': {
    position: 'absolute',
    bottom: 'calc(100% + 6px)',
    left: 0,
    width: 340,
    bgcolor: 'rgba(33, 33, 33, 0.92)',
    color: '#fff',
    fontSize: '0.75rem',
    lineHeight: 1.4,
    px: 1.25,
    py: 0.75,
    borderRadius: 1,
    pointerEvents: 'none',
    zIndex: 1500,
    visibility: 'hidden',
    opacity: 0,
    transition: 'opacity 0.15s',
    '& ul': { m: 0, mt: 0.5, pl: 2 }
  },
  '&:hover .fetch-tip, &:focus-within .fetch-tip': { visibility: 'visible', opacity: 1 }
};

const count = (n) =>(typeof n === 'number' ? n.toLocaleString() : '—');
const utc = (iso) => (iso ? new Date(iso).toISOString().slice(0, 16).replace('T', ' ') : '—');
const duration = (sec) => {
  if (!sec) return '0m';
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d) return `${d}d ${h}h ${m}m`;
  if (h) return `${h}h ${m}m`;
  return m ? `${m}m` : `${Math.round(sec)}s`;
};

// ---- Episodes popup -----------------------------------------------------------------------------

const PopupScroller = React.forwardRef((props, ref) => (
  <TableContainer component={Paper} variant="outlined" ref={ref} {...props} sx={{ borderRadius: '10px' }} />
));
// Fixed column widths and one-line cells keep every row the same height. With auto layout the
// columns resize as different rows scroll into view, rows change height, and the virtualized list
// flickers and loses its scroll position.
const PopupTable = (props) => <Table {...props} size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: '150ch' }} />;
const PopupTableRow = ({ item, ...props }) => <TableRow {...props} />;
const PopupTableBody = React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />);
const popupComponents = { Scroller: PopupScroller, Table: PopupTable, TableHead, TableRow: PopupTableRow, TableBody: PopupTableBody };

const ONE_LINE_SX = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const EPISODE_COLUMNS = [
  ['Monitor ID', 'left', '12ch'], ['Monitor', 'left', '30ch'], ['Group', 'left', '24ch'], ['Started (UTC)', 'left', '17ch'],
  ['Recovered (UTC)', 'left', '17ch'], ['Peak', 'left', '7ch'], ['Error triggers', 'right', '9ch'], ['Warn triggers', 'right', '9ch'],
  ['Time in Error', 'right', '13ch'], ['Time in Warn', 'right', '13ch']
];

/** Every episode saved by one fetch. */
export function ReportBatchEpisodesPopup({ batch, onClose }) {
  const [state, setState] = useState({ loading: false, error: '', total: 0, episodes: [] });
  const batchId = batch?.id;

  useEffect(() => {
    if (!batchId) return undefined;
    let cancelled = false;
    setState({ loading: true, error: '', total: 0, episodes: [] });
    authGet(`${API}/${batchId}/episodes`, null, 'json', { useCache: false })
      .then((data) => { if (!cancelled) setState({ loading: false, error: '', total: data.total, episodes: data.episodes || [] }); })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || 'Failed to load the episodes', total: 0, episodes: [] }); });
    return () => { cancelled = true; };
  }, [batchId]);

  const { loading, error, total, episodes } = state;
  const anyTruncated = episodes.some((e) => e.truncatedStart);
  return (
    <PopupDialog
      open={!!batch}
      onClose={onClose}
      maxWidth="xl"
      title={batch ? `Episodes saved: ${batch.startDate} to ${batch.endDate}` : ''}
    >
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>}
      {error && <Alert severity="error">{error}</Alert>}
      {!loading && !error && (
        <>
          <Typography sx={{ color: MUTED, fontSize: '0.8125rem', mb: 1 }}>
            {count(total)} episode{total === 1 ? '' : 's'}
            {total > episodes.length && ` (showing the first ${count(episodes.length)}; use the CSV download for all of them)`}
          </Typography>
          <TableVirtuoso
            style={{ height: 520 }}
            data={episodes}
            computeItemKey={(_i, e) => e._id}
            components={popupComponents}
            fixedHeaderContent={() => (
              <TableRow sx={{ bgcolor: '#fff' }}>
                {EPISODE_COLUMNS.map(([label, align, width]) => <TableCell key={label} align={align} sx={{ ...ONE_LINE_SX, width }}>{label}</TableCell>)}
              </TableRow>
            )}
            itemContent={(_i, e) => (
              <>
                <TableCell sx={ONE_LINE_SX} title={e.monitorId}>{e.monitorId}</TableCell>
                <TableCell sx={ONE_LINE_SX} title={e.monitorName || ''}>{e.monitorName || '—'}</TableCell>
                <TableCell sx={ONE_LINE_SX} title={e.group}>{e.group}</TableCell>
                <TableCell sx={ONE_LINE_SX}>{utc(e.start)}{e.truncatedStart && ' *'}</TableCell>
                <TableCell sx={ONE_LINE_SX}>{e.end ? utc(e.end) : <b>open</b>}</TableCell>
                <TableCell sx={ONE_LINE_SX}>{e.peak === 'Alert' ? 'Error' : 'Warn'}</TableCell>
                <TableCell align="right" sx={ONE_LINE_SX}>{e.alerts}</TableCell>
                <TableCell align="right" sx={ONE_LINE_SX}>{e.warns}</TableCell>
                <TableCell align="right" sx={ONE_LINE_SX}>{duration(e.errT)}</TableCell>
                <TableCell align="right" sx={ONE_LINE_SX}>{duration(e.warnT)}</TableCell>
              </>
            )}
          />
          {anyTruncated && (
            <Typography sx={{ color: MUTED, fontSize: '0.75rem', mt: 1 }}>
              * Already open when the date range began; Started is the first event in the range.
            </Typography>
          )}
        </>
      )}
    </PopupDialog>
  );
}

// ---- Monthly report month -------------------------------------------------------------------------

const MONTH_LABEL_FORMAT = 'MMMM YYYY';

/**
 * Asks which month to report. Only months that were fetched in full and have no saved report yet are offered; a month
 * with a report is read on the Trends tab. Every report compares the chosen month with the month before it.
 */
function MonthDialog({ open, onClose, onRun }) {
  const [state, setState] = useState({ loading: false, error: '', months: [] });
  const [month, setMonth] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState({ loading: true, error: '', months: [] });
    authGet(`${MONTHLY_REPORT_API}/months`, null, 'json', { useCache: false })
      .then((data) => {
        if (cancelled) return;
        const all = data.months || [];
        // Newest first (the server sends them oldest first).
        const months = all.filter((m) => !m.partial && !m.savedAt).reverse();
        let error = '';
        if (!all.some((m) => !m.partial)) error = 'No month has been fetched in full yet. Fetch monitor events for a month first.';
        else if (!months.length) error = 'Every fetched month already has a report. Open it on the Trends tab.';
        setState({ loading: false, error, months });
        // The most recent month is the likely one.
        setMonth(months.length ? months[0].month : '');
      })
      .catch((err) => { if (!cancelled) setState({ loading: false, error: err.message || 'Failed to list the months', months: [] }); });
    return () => { cancelled = true; };
  }, [open]);

  const { loading, error, months } = state;
  const chosen = months.find((m) => m.month === month);
  const previous = chosen ? dayjs(`${chosen.month}-01`).subtract(1, 'month') : null;
  return (
    <PopupDialog open={open} onClose={onClose} title="Generate Monthly Report" maxWidth="sm" showClose={false}>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
      {error && <Alert severity="info">{error}</Alert>}
      {months.length > 0 && (
        <>
          <Typography sx={{ color: MUTED, fontSize: '0.8125rem', mb: 2 }}>
            Pick the month to report. Only months that have been fetched and have no report yet are listed.
          </Typography>
          <TextField
            select
            size="small"
            label="Month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            sx={{ width: 260 }}
          >
            {months.map((m) => <MenuItem key={m.month} value={m.month}>{m.label}</MenuItem>)}
          </TextField>
          {chosen && (
            <Typography sx={{ fontSize: '0.8125rem', mt: 2 }}>
              The report covers all {chosen.of} days of {dayjs(`${chosen.month}-01`).format(MONTH_LABEL_FORMAT)} and compares them with {previous.format(MONTH_LABEL_FORMAT)}.
            </Typography>
          )}
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!chosen}
          onClick={() => onRun(month)}
          sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT, filter: 'brightness(0.9)' } }}
        >
          Generate
        </Button>
      </Box>
    </PopupDialog>
  );
}

// ---- Download range -----------------------------------------------------------------------------

/**
 * Asks which days to download. The earliest Start Date and latest End Date are the first and last days that have
 * saved events (the server checks the range again). The CSV has the episodes that start in the range.
 */
function DownloadRangeDialog({ open, busy, onClose, onDownload }) {
  const [bounds, setBounds] = useState({ loading: false, error: '', min: null, max: null });
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setBounds({ loading: true, error: '', min: null, max: null });
    authGet(`${EPISODES_API}/range`, null, 'json', { useCache: false })
      .then((data) => {
        if (cancelled) return;
        const min = data.minDate ? dayjs(data.minDate) : null;
        const max = data.maxDate ? dayjs(data.maxDate) : null;
        setBounds({ loading: false, error: min ? '' : 'There are no saved events to download yet.', min, max });
        // Start with everything that can be downloaded.
        setFrom(min);
        setTo(max);
      })
      .catch((err) => { if (!cancelled) setBounds({ loading: false, error: err.message || 'Failed to read the range of saved events', min: null, max: null }); });
    return () => { cancelled = true; };
  }, [open]);

  const { loading, error, min, max } = bounds;
  const valid = (d) => !!d && d.isValid() && !!min && !d.isBefore(min, 'day') && !d.isAfter(max, 'day');
  const rangeOk = valid(from) && valid(to) && !from.isAfter(to, 'day');
  const field = (label, value, onChange, minDate, maxDate) => (
    <DatePicker
      label={label}
      value={value}
      onChange={onChange}
      minDate={minDate}
      maxDate={maxDate}
      format={DATE_FORMAT}
      disabled={!min || busy}
      slotProps={{ textField: { size: 'small', sx: { width: 190 }, helperText: 'Calendar day, UTC-5' } }}
    />
  );

  return (
    <PopupDialog open={open} onClose={onClose} title="Download CSV file" maxWidth="sm" showClose={false}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
        {error && <Alert severity={min ? 'error' : 'info'}>{error}</Alert>}
        {min && (
          <>
            <Typography sx={{ color: MUTED, fontSize: '0.8125rem', mb: 2 }}>
              Pick the days to download. Saved events cover {min.format(DATE_FORMAT)} to {max.format(DATE_FORMAT)}, so the range has to be inside it.
              The file has the episodes that start in the range.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {field('Start Date', from, setFrom, min, to && to.isValid() ? to : max)}
              {field('End Date', to, setTo, from && from.isValid() ? from : min, max)}
            </Box>
          </>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 3 }}>
          {busy && <CircularProgress size={16} aria-label="Preparing download" />}
          <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!rangeOk || busy}
            onClick={() => onDownload(from.format(DATE_FORMAT), to.format(DATE_FORMAT))}
            sx={{ textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT, filter: 'brightness(0.9)' } }}
          >
            Download
          </Button>
        </Box>
      </LocalizationProvider>
    </PopupDialog>
  );
}

// ---- Tab panel ----------------------------------------------------------------------------------

const HISTORY_COLUMNS = [
  ['Batch ID', 'left'], ['Fetched', 'left'], ['Start date', 'left'], ['End date', 'left'], ['Events', 'right'], ['Error triggers', 'right'],
  ['Warn triggers', 'right'], ['Episodes', 'right'], ['Monitors', 'right'], ['Groups', 'right']
];

/** Content of the Generate Report tab: pick a date range, fetch its monitor events, browse the history of fetches. */
export default function GenerateReportPanel() {
  // The month to fetch, picked as a month (1-12) and a year; '' until picked.
  const [fetchMonth, setFetchMonth] = useState('');
  const [fetchYear, setFetchYear] = useState('');
  const [batches, setBatches] = useState([]);
  const [runningId, setRunningId] = useState(null);
  const [error, setError] = useState('');
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportInfo, setReportInfo] = useState('');
  const [monthOpen, setMonthOpen] = useState(false);

  const [fetchedDates, setFetchedDates] = useState(() => new Set());

  const lastMonth = useMemo(lastFetchableMonth, []);
  // The months with every day fetched. They cannot be fetched again (the server refuses it too).
  const fetchedMonths = useMemo(() => {
    const days = new Map();
    fetchedDates.forEach((day) => days.set(day.slice(0, 7), (days.get(day.slice(0, 7)) || 0) + 1));
    return new Set([...days].filter(([key, n]) => n >= dayjs(`${key}-01`).daysInMonth()).map(([key]) => key));
  }, [fetchedDates]);
  // A month can be picked from July 2026 up to the month before the current one, if it has not been fetched.
  const monthKeyOf = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
  const monthSelectable = useCallback((key) => key >= FIRST_MONTH.format(MONTH_KEY_FORMAT) && key <= lastMonth.format(MONTH_KEY_FORMAT) && !fetchedMonths.has(key), [lastMonth, fetchedMonths]);
  const years = Array.from({ length: lastMonth.year() - FIRST_MONTH.year() + 1 }, (_, i) => FIRST_MONTH.year() + i);
  const fetchKey = fetchMonth && fetchYear ? monthKeyOf(fetchYear, fetchMonth) : null;
  const monthValid = !!fetchKey && monthSelectable(fetchKey);
  const running = runningId !== null;

  // Fetching monitor events is admin-only (the server enforces it too).
  const [canFetch] = useState(() => !!isAdmin());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // History, and a fetch that is still running (started before a reload or from another tab).
  useEffect(() => {
    let cancelled = false;
    authGet(API, null, 'json', { useCache: false })
      .then((data) => {
        if (cancelled) return;
        setBatches(data.batches || []);
        setFetchedDates(new Set(data.fetchedDates || []));
        if (data.running) setRunningId(data.running.id);
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load the fetch history'); })
      .finally(() => { if (!cancelled) setHistoryLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // While a fetch runs, poll it until it is saved (or has failed).
  useEffect(() => {
    if (!runningId) return undefined;
    let cancelled = false;
    let timer;
    let failures = 0;
    const poll = async () => {
      try {
        const data = await authGet(`${API}/${runningId}`, null, 'json', { useCache: false });
        if (cancelled) return;
        failures = 0;
        const batch = data.batch;
        if (batch.status === 'running') {
          timer = setTimeout(poll, POLL_MS);
          return;
        }
        setRunningId(null);
        if (batch.status === 'completed') {
          setBatches((prev) => [batch, ...prev.filter((b) => b.id !== batch.id)]);
          // Back to empty, which also leaves Fetch monitor events disabled until another month is picked.
          // A failed fetch keeps the month so it can be retried.
          setFetchMonth('');
          setFetchYear('');
          // The month just fetched can no longer be picked. Guarded by `mounted`, not `cancelled`: setRunningId(null)
          // above has already run this effect's cleanup.
          authGet(API, null, 'json', { useCache: false })
            .then((list) => { if (mounted.current) setFetchedDates(new Set(list.fetchedDates || [])); })
            .catch(() => {});
        } else setError(batch.error || 'The fetch failed.');
      } catch (err) {
        if (cancelled) return;
        failures += 1;
        if (failures >= MAX_POLL_FAILURES) {
          setRunningId(null);
          setError(`Lost track of the fetch: ${err.message || 'request failed'}. Reload to see whether it finished.`);
          return;
        }
        timer = setTimeout(poll, POLL_MS);
      }
    };
    timer = setTimeout(poll, POLL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [runningId]);

  const handleFetch = useCallback(async () => {
    setError('');
    try {
      const data = await authPost(API, { month: fetchKey });
      setRunningId(data.batch.id);
    } catch (err) {
      setError(err.message || 'Failed to start the fetch');
    }
  }, [fetchKey]);

  // Download CSV asks for the range first (see DownloadRangeDialog); this saves the file once a range is chosen.
  const handleDownload = useCallback(async (startDay, endDay) => {
    setDownloading(true);
    setError('');
    try {
      const blob = await authGet(`${EPISODES_API}/csv`, { startDate: startDay, endDate: endDay }, 'blob', { useCache: false });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `datadog_monitor_episodes_${startDay}_to_${endDay}.csv`;
      link.click();
      // Some browsers start the download asynchronously; revoking right away can cancel it.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setDownloadOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to download the CSV file');
    } finally {
      setDownloading(false);
    }
  }, []);

  // Builds the overall report for the chosen month (see MonthDialog) from the saved events, saves it in monthly-report/
  // on the server, and opens it. The tab is opened first, inside the click, so the browser does not block it.
  const handleGenerateReport = useCallback(async (month) => {
    const tab = window.open('', '_blank');
    setGeneratingReport(true);
    setMonthOpen(false);
    setError('');
    setReportInfo('');
    try {
      const result = await authPost(MONTHLY_REPORT_API, { month });
      setReportInfo(result.existing
        ? `Opened the saved ${result.fileName} (saved ${new Date(result.savedAt).toLocaleString()}). It was not rebuilt.`
        : `Saved ${result.fileName} (${count(result.episodesInMonth)} episodes in the month, ${count(result.monitors)} monitors).`);
      const blob = await authGet(`${MONTHLY_REPORT_API}/${result.fileName}`, null, 'blob', { useCache: false });
      // Shown in a sandboxed frame (as the Trends tab does) instead of navigating the tab to a blob: URL, which would run
      // the report's script with this app's origin and so with access to the signed-in user's storage.
      const html = await new Blob([blob]).text();
      if (tab) {
        const doc = tab.document;
        doc.title = 'Monthly report';
        doc.body.style.margin = '0';
        const frame = doc.createElement('iframe');
        frame.setAttribute('sandbox', 'allow-scripts allow-popups allow-popups-to-escape-sandbox');
        frame.style.cssText = 'border:0;width:100vw;height:100vh;display:block';
        frame.srcdoc = html;
        doc.body.appendChild(frame);
      }
    } catch (err) {
      if (tab) tab.close();
      setError(err.message || 'Failed to generate the monthly report');
    } finally {
      setGeneratingReport(false);
    }
  }, []);

  // Changing the year drops a month that cannot be picked in that year.
  const handleYearChange = (year) => {
    setFetchYear(year);
    if (fetchMonth && !monthSelectable(monthKeyOf(year, fetchMonth))) setFetchMonth('');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ minWidth: 960 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <TextField
            select
            size="small"
            label="Year"
            value={fetchYear}
            onChange={(e) => handleYearChange(e.target.value)}
            disabled={running || !canFetch}
            sx={{ width: 120 }}
          >
            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            label="Month"
            value={fetchMonth}
            onChange={(e) => setFetchMonth(e.target.value)}
            disabled={running || !canFetch || !fetchYear}
            helperText={fetchYear ? 'Whole months only, UTC-5' : 'Pick the year first'}
            sx={{ width: 190 }}
          >
            {MONTH_NAMES.map((name, i) => {
              const key = monthKeyOf(fetchYear, i + 1);
              return (
                <MenuItem key={name} value={i + 1} disabled={!monthSelectable(key)}>
                  {name}{fetchedMonths.has(key) ? ' (fetched)' : ''}
                </MenuItem>
              );
            })}
          </TextField>
          <Box component="span" sx={FETCH_TIP_WRAPPER_SX}>
            <Button
              variant="contained"
              onClick={handleFetch}
              disabled={!canFetch || !monthValid || running}
              aria-describedby="fetch-checks-tip"
              sx={{ textTransform: 'none', height: 40, bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT, filter: 'brightness(0.9)' } }}
            >
              Fetch monitor events
            </Button>
            <Box component="div" id="fetch-checks-tip" role="tooltip" className="fetch-tip">
              {!canFetch && <strong>Only admins can fetch monitor events.</strong>}
              <div>Checks before a fetch runs:</div>
              <ul>
                {FETCH_CHECKS.map((check) => <li key={check}>{check}</li>)}
              </ul>
            </Box>
          </Box>
          <Button
            variant="outlined"
            onClick={() => setMonthOpen(true)}
            disabled={running || generatingReport}
            sx={{ textTransform: 'none', height: 40, color: ACCENT, borderColor: ACCENT, '&:hover': { borderColor: ACCENT } }}
          >
            Generate Monthly Report
          </Button>
          <Box sx={{ height: 40, display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconActionButton
              label="Download CSV file"
              icon={<DownloadIcon fontSize="small" />}
              disabled={!batches.length || running || downloading}
              onClick={() => setDownloadOpen(true)}
            />
            {(running || downloading || generatingReport) && <CircularProgress size={16} aria-label={running ? 'Fetching monitor events' : generatingReport ? 'Generating the monthly report' : 'Preparing download'} />}
            {running && <Typography sx={{ color: MUTED, fontSize: '0.8125rem' }}>Fetching and saving events…</Typography>}
          </Box>
        </Box>

        {reportInfo && <Alert severity="success" onClose={() => setReportInfo('')} sx={{ mb: 2, maxWidth: 720 }}>{reportInfo}</Alert>}
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2, maxWidth: 720 }}>{error}</Alert>}

        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: INK, mb: 1 }}>Fetch history</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', maxHeight: 460 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {HISTORY_COLUMNS.map(([label, align]) => <TableCell key={label} align={align} sx={{ whiteSpace: 'nowrap', bgcolor: '#fff' }}>{label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((b) => (
                <TableRow
                  key={b.id}
                  hover
                  tabIndex={0}
                  role="button"
                  aria-label={`Show the episodes saved for ${b.startDate} to ${b.endDate}`}
                  onClick={() => setSelectedBatch(b)}
                  onKeyDown={(e) => {
                    if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
                    e.preventDefault();
                    setSelectedBatch(b);
                  }}
                  sx={{ cursor: 'pointer', '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: -2 } }}
                >
                  <TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>{b.id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(b.fetchedAt).toLocaleString()}</TableCell>
                  <TableCell>{b.startDate}</TableCell>
                  <TableCell>{b.endDate}</TableCell>
                  <TableCell align="right">{count(b.totalEvents)}</TableCell>
                  <TableCell align="right">{count(b.totalErrorTriggers)}</TableCell>
                  <TableCell align="right">{count(b.totalWarnTriggers)}</TableCell>
                  <TableCell align="right">{count(b.totalEpisodes)}</TableCell>
                  <TableCell align="right">{count(b.uniqueMonitors)}</TableCell>
                  <TableCell align="right">{count(b.uniqueGroups)}</TableCell>
                </TableRow>
              ))}
              {!batches.length && (
                <TableRow>
                  <TableCell colSpan={HISTORY_COLUMNS.length} sx={{ color: MUTED, bgcolor: NEUTRAL_SURFACE }}>
                    {historyLoaded ? 'Nothing fetched yet. Pick a year and month, then Fetch monitor events.' : 'Loading…'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      <ReportBatchEpisodesPopup batch={selectedBatch} onClose={() => setSelectedBatch(null)} />
      <MonthDialog open={monthOpen} onClose={() => setMonthOpen(false)} onRun={handleGenerateReport} />
      <DownloadRangeDialog open={downloadOpen} busy={downloading} onClose={() => setDownloadOpen(false)} onDownload={handleDownload} />
    </LocalizationProvider>
  );
}
